"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProofDropField } from "@/components/payments/proof-drop-field";
import { uploadPaymentProof } from "@/lib/lead-media-api";

type ReceiptTarget = { id: string; lead_id?: string | null; proof_count?: number; method?: string; settlement_status?: string | null };

/**
 * ArtDent 260930 — file the POS slip against a card payment. Nothing about the
 * money changes; this is the paperwork chase, offered where the "No proof"
 * nudge points. Dropped, pasted or picked, like the patient's Media & Files.
 *
 * ArtDent 261003 — and the bank's confirmation against an IBAN transfer, from
 * its own Add attachment button rather than through Correct. On a pending
 * transfer, filing may receive it where the clinic asked for that (and the
 * person filing may receive money); the toast says which happened.
 */
export function AttachReceiptDialog({ payment, onOpenChange, onSuccess }: { payment: ReceiptTarget | null; onOpenChange: (open: boolean) => void; onSuccess: () => void }) {
  const t = useTranslations("payments");
  const tm = useTranslations("money");
  const tc = useTranslations("common");
  const [proof, setProof] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (payment) setProof(null); }, [payment?.id]);
  const transfer = payment?.method === "bank-transfer";

  const submit = async () => {
    if (!payment?.lead_id || !proof) return;
    setSubmitting(true);
    try {
      const filed = await uploadPaymentProof(payment.lead_id, proof, payment.id);
      toast.success(payment.settlement_status === "pending"
        ? tm(filed.settlementStatus === "received" ? "proofConfirmed" : "proofFiledPending")
        : t("attachReceipt.done"));
      onOpenChange(false);
      onSuccess();
    } catch {
      toast.error(t("attachReceipt.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!payment} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{t("attachReceipt.title")}</DialogTitle></DialogHeader>
        <ProofDropField
          label={t(transfer ? "attachReceipt.labelTransfer" : "attachReceipt.label")}
          hint={(payment?.proof_count ?? 0) > 0 ? t("recordPaymentDialog.proofAttached", { count: payment?.proof_count ?? 0 }) : t(transfer ? "attachReceipt.hintTransfer" : "attachReceipt.hint")}
          value={proof}
          onChange={setProof}
          disabled={submitting}
          dropText={transfer ? undefined : t("attachReceipt.drop")}
          namePrefix={transfer ? "bank-confirmation" : "pos-receipt"}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{tc("cancel")}</Button>
          <Button onClick={submit} disabled={!proof || submitting}>{t("attachReceipt.title")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
